<template>
  <div class="chat-view-container">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-left">
        <button @click="goBack" class="btn-back">← Back</button>
        <div class="header-title">
          <h1>💬 Task Chat: {{ task?.title || 'Loading...' }}</h1>
          <div class="chat-meta">
            <span v-if="selectedModel" class="model-badge">Model: {{ getModelName(selectedModel) }}</span>
            <span v-if="selectedAgentId" class="agent-badge">Agent Active</span>
          </div>
        </div>
      </div>

      <div class="header-right">
        <button class="btn btn-secondary btn-sm" @click="refreshData" :disabled="loading">
          Refresh
        </button>
      </div>
    </div>

    <div v-if="chatDrawerOpen" class="drawer-overlay" @click="chatDrawerOpen = false"></div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading task...</p>
    </div>

    <!-- Chat Content -->
    <div v-else-if="task" class="chat-content">
      <!-- Main Chat Layout -->
      <div class="main-layout">
        <!-- Left Column - Task Context -->
        <div class="context-section">
          <!-- Task Information -->
          <div class="section-card">
            <div class="section-header">
              <h2>📝 Task Information</h2>
              <button @click="showEditTask = true" class="btn btn-sm btn-outline">
                <i class="icon-edit">✏️</i> Edit
              </button>
            </div>
            <div class="section-content">
              <div class="info-row">
                <span class="info-label">Description:</span>
                <p class="info-value">{{ task.description || 'No description provided' }}</p>
              </div>
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span :class="['status-badge', task.status || 'pending']">
                  {{ task.status || 'Pending' }}
                </span>
              </div>
              <div class="info-row" v-if="task.assigned_to">
                <span class="info-label">Assigned To:</span>
                <span class="info-value">{{ task.assigned_to }}</span>
              </div>
              <div class="info-row" v-if="task.workstream_id">
                <span class="info-label">Workstream:</span>
                <router-link
                  :to="{ name: 'workstream-detail', params: { id: task.workstream_id } }"
                  class="meta-link mono"
                >
                  {{ task.workstream_id }}
                </router-link>
              </div>

              <div v-if="task.goal_id" class="meta-item">
                <div class="meta-label">Goal</div>
                <router-link
                  :to="{ name: 'goal-detail', params: { id: task.goal_id }, query: task.workstream_id ? { ws: task.workstream_id } : {} }"
                  class="meta-link mono"
                >
                  {{ task.goal_id }}
                </router-link>
              </div>

              <div class="meta-item">
                <div class="meta-label">Due</div>
                <div class="meta-value">{{ dueDateLabel }}</div>
              </div>

              <div class="meta-item">
                <div class="meta-label">Tokens</div>
                <div class="meta-value">{{ tokenAllocatedLabel }}</div>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-title-row">
              <h2 class="card-title">Assigned agents</h2>
              <button class="btn btn-secondary btn-sm" @click="showAssignAgent = true">Assign</button>
            </div>
            <div class="section-content">
              <div v-if="assignedAgents.length === 0" class="empty-state-small">
                <p>No agents assigned yet. Assign an agent to help with this task.</p>
              </div>
              <div v-else class="agents-list">
                <div v-for="agent in assignedAgents" :key="agent.id" class="agent-item">
                  <div class="agent-icon">🤖</div>
                  <div class="agent-info">
                    <h4>{{ agent.name || agent.id }}</h4>
                    <p>{{ agent.role || 'AI Agent' }}</p>
                  </div>
                  <button
                    @click="removeAgent(agent.id)"
                    class="btn-remove"
                    title="Remove agent"
                  >
                    ×
                  </button>
                </div>
                <button class="btn btn-secondary btn-sm" @click="removeAgent(agent.id)">Remove</button>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-title-row">
              <h2 class="card-title">Actions</h2>
            </div>
            <div class="section-content">
              <div class="actions-list">
                <button
                  v-if="task.status !== 'completed' && task.status !== 'done'"
                  @click="completeTask"
                  class="action-btn success"
                >
                  <i class="icon">✔️</i>
                  <span>Mark as Complete</span>
                </button>
                <button
                  v-if="task.assigned_to !== currentUserId"
                  @click="assignToMe"
                  class="action-btn primary"
                >
                  <i class="icon">👤</i>
                  <span>Assign to Me</span>
                </button>
                <button @click="showEditTask = true" class="action-btn secondary">
                  <i class="icon">✏️</i>
                  <span>Edit Task</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Main Chat -->
        <div class="chat-section">
          <UnifiedChat
            chat-type="task"
            :target-id="taskId"
            :model-id="selectedModel"
            :messages="taskChatHistory"
            :is-typing="false"
            :is-loading="taskChatLoading"
            :disabled="!selectedModel"
            :width="400"
            :show-model-selector="false"
            :available-models="models"
            @send-message="sendTaskChat"
            @quick-prompt="handleTaskQuickPrompt"
            @model-change="handleModelChange"
            @settings="handleTaskSettings"
          />
        </div>
      </div>
    </div>

    <!-- Assign Agent Modal -->
    <div v-if="showAssignAgent" class="modal-overlay" @click="showAssignAgent = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Assign Agent to Task</h2>
          <button @click="showAssignAgent = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="addAgent">
            <div class="form-group">
              <label for="agent_id">Agent ID *</label>
              <input
                id="agent_id"
                v-model="newAgentId"
                type="text"
                placeholder="Enter agent ID"
                required
              />
            </div>
            <div class="form-actions">
              <button type="button" @click="showAssignAgent = false" class="btn btn-secondary">Cancel</button>
              <button type="submit" class="btn btn-primary" :disabled="!newAgentId">Assign Agent</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Edit Task Modal -->
    <div v-if="showEditTask" class="modal-overlay" @click="showEditTask = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>Edit Task</h2>
          <button @click="showEditTask = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="updateTask">
            <div class="form-group">
              <label for="title">Title *</label>
              <input
                id="title"
                v-model="editedTask.title"
                type="text"
                placeholder="Enter task title"
                required
              />
            </div>
            <div class="form-group">
              <label for="description">Description</label>
              <textarea
                id="description"
                v-model="editedTask.description"
                rows="4"
                placeholder="Enter task description"
              ></textarea>
            </div>
            <div class="form-group">
              <label for="status">Status</label>
              <select id="status" v-model="editedTask.status">
                <option value="todo">Todo</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div class="form-group">
              <label for="goal_id">Goal</label>
              <select id="goal_id" v-model="editedTask.goal_id">
                <option :value="null">No goal</option>
                <option v-for="g in goals" :key="g.id" :value="g.id">{{ g.description || g.title || g.name || g.id }}</option>
              </select>
            </div>
            <div class="form-group">
              <label for="allocated_tokens">Allocated Tokens</label>
              <input
                id="allocated_tokens"
                v-model.number="editedTask.allocated_tokens"
                type="number"
                min="1"
                max="100"
                step="1"
                placeholder="Enter tokens (1-100)"
              />
            </div>
            <div class="form-group">
              <label for="assigned_to">Assigned To</label>
              <input
                id="assigned_to"
                v-model="editedTask.assigned_to"
                type="text"
                placeholder="Enter assignee email"
              />
            </div>
            <div class="form-actions">
              <button type="button" @click="showEditTask = false" class="btn btn-secondary">Cancel</button>
              <button type="submit" class="btn btn-primary">Update Task</button>
            </div>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import UnifiedChat from '@/components/UnifiedChat.vue'
import { chatApi, goalsApi, llmApi, api } from '@/services/api'

export default {
  name: 'ChatView',
  components: { UnifiedChat },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const taskId = computed(() => route.params.id)
    const task = computed(() => store.state.tasks?.currentItem)
    const loading = ref(false)
    const assignedAgents = ref([])
    const showAssignAgent = ref(false)
    const showEditTask = ref(false)
    const newAgentId = ref('')
    const selectedAgentId = ref('')
    const selectedModel = ref('')
    const models = ref([])
    const taskChatHistory = ref([])
    const taskChatLoading = ref(false)
    const currentUserId = computed(() => store.state.auth.user?.id)

    const editedTask = ref({
      title: '',
      description: '',
      status: 'todo',
      goal_id: null,
      allocated_tokens: 100,
      assigned_to: ''
    })

    const goals = ref([])

    const loadModels = async () => {
      try {
        const res = await llmApi.getModels()
        models.value = Array.isArray(res) ? res : (res?.models || [])
        if (models.value.length > 0 && !selectedModel.value) {
          selectedModel.value = models.value[0].id
        }
      } catch (e) {
        console.warn('Failed to load models', e)
      }
    }

    const loadTask = async () => {
      try {
        await store.dispatch('tasks/fetchById', taskId.value)
      } catch (error) {
        console.error('Failed to load task:', error)
      }
    }

    const refreshData = async () => {
      loading.value = true
      try {
        await store.dispatch('tasks/fetchById', taskId.value)
        if (task.value) {
          editedTask.value = {
            title: task.value.title || '',
            description: task.value.description || '',
            status: (task.value.status === 'completed' ? 'done' : (task.value.status === 'in-progress' ? 'in_progress' : (task.value.status || 'todo'))),
            goal_id: task.value.goal_id || null,
            allocated_tokens: task.value.token_reward || task.value.token_allocated || task.value.allocated_tokens || 100,
            assigned_to: task.value.assigned_to || task.value.assignee_name || ''
          }
          if (task.value.workstream_id) {
            const res = await goalsApi.listByWorkstream(task.value.workstream_id)
            goals.value = Array.isArray(res) ? res : (res?.goals || [])
          }
          assignedAgents.value = task.value.agents || []
          if (assignedAgents.value.length > 0) {
            selectedAgentId.value = assignedAgents.value[0].id
          }
        }
      } finally {
        loading.value = false
      }
    }

    const addAgent = async () => {
      try {
        await store.dispatch('tasks/addAgent', {
          id: taskId.value,
          agent_id: newAgentId.value
        })
        showAssignAgent.value = false
        newAgentId.value = ''
        await refreshData()
      } catch (error) {
        console.error('Failed to assign agent:', error)
        alert('Failed to assign agent. Please check agent ID and try again.')
      }
    }

    const removeAgent = async (agentId) => {
      if (!confirm('Are you sure you want to remove this agent?')) return
      try {
        await store.dispatch('tasks/removeAgent', {
          id: taskId.value,
          agent_id: agentId
        })
        await refreshData()
      } catch (error) {
        console.error('Failed to remove agent:', error)
      }
    }

    const completeTask = async () => {
      try {
        await store.dispatch('tasks/complete', {
          id: taskId.value,
          data: {}
        })
        await refreshData()
      } catch (error) {
        console.error('Failed to complete task:', error)
      }
    }

    const assignToMe = async () => {
      if (!currentUserId.value) {
        alert('Unable to determine current user')
        return
      }
      try {
        await store.dispatch('tasks/assign', {
          id: taskId.value,
          assigned_to: currentUserId.value
        })
        await refreshData()
      } catch (error) {
        console.error('Failed to assign task:', error)
      }
    }

    const updateTask = async () => {
      try {
        const updatePayload = {
          title: editedTask.value.title,
          description: editedTask.value.description,
          status: editedTask.value.status,
          goal_id: editedTask.value.goal_id
        }

        if (editedTask.value.allocated_tokens !== undefined && editedTask.value.allocated_tokens !== null) {
          updatePayload.token_allocated = editedTask.value.allocated_tokens
        }

        if (editedTask.value.assigned_to) {
          updatePayload.assigned_to = editedTask.value.assigned_to
        }

        await store.dispatch('tasks/update', {
          id: taskId.value,
          updates: updatePayload
        })
        showEditTask.value = false
        await refreshData()
      } catch (error) {
        console.error('Failed to update task:', error)
      }
    }

    const sendTaskChat = async (message) => {
      let processedMessage
      if (typeof message === 'string') {
        processedMessage = message
      } else if (message.message) {
        processedMessage = message.message
      } else if (message.label || message.text) {
        processedMessage = message.label || message.text
      } else {
        processedMessage = String(message)
      }

      if (!processedMessage) return

      const token = localStorage.getItem('token')
      if (!token) {
        taskChatHistory.value.push({
          role: 'assistant',
          content: 'You must be logged in to use chat functionality. Please log in and try again.'
        })
        return
      }

      if (!selectedModel.value) {
        taskChatHistory.value.push({
          role: 'assistant',
          content: 'Please select a model before sending messages.'
        })
        return
      }

      taskChatHistory.value.push({ role: 'user', content: processedMessage })
      taskChatLoading.value = true

      try {
        const taskChatResponse = await api.post(`/api/chat/task/${taskId.value}`, {
          message: processedMessage,
          chat_mode: 'model',
          model_id: selectedModel.value,
          task_context: {
            title: task.value?.title || 'Unknown Task',
            description: task.value?.description || '',
            status: task.value?.status || 'todo',
            id: taskId.value
          }
        })

        console.log('Task chat API response:', taskChatResponse)

        const responseMessage = taskChatResponse?.message || taskChatResponse?.data?.message || 'I can help you with this task.'
        console.log('Using response message:', responseMessage)

        taskChatHistory.value.push({
          role: 'assistant',
          content: responseMessage
        })
      } catch (error) {
        console.error('Task chat error:', error)

        let errorContent = 'Sorry, I encountered an error. Please try again.'

        if (error.response) {
          const status = error.response.status
          if (status === 401) {
            errorContent = 'Authentication required. Please log in and try again.'
          } else if (status === 403) {
            errorContent = 'You do not have permission to perform this action.'
          } else if (status === 400) {
            errorContent = 'Invalid request. Please check your message and try again.'
          } else if (status === 500) {
            errorContent = 'Server error. Please try again later.'
          }
        }

        taskChatHistory.value.push({
          role: 'assistant',
          content: errorContent
        })
      } finally {
        taskChatLoading.value = false
      }
    }

    const handleTaskQuickPrompt = (payload) => {
      sendTaskChat(payload)
    }

    const handleTaskSettings = () => {
      console.log('Settings clicked for task chat')
    }

    const handleModelChange = (modelId) => {
      selectedModel.value = modelId
    }

    const getModelName = (modelId) => {
      if (!modelId || !models.value.length) return 'No Model'
      const model = models.value.find(m => m.id === modelId)
      return model ? model.name : 'Unknown Model'
    }

    const goBack = () => {
      const workstreamId = route.query.workstream_id || task.value?.workstream_id
      if (workstreamId) {
        router.push({ name: 'workstream-detail', params: { id: workstreamId } })
      } else {
        router.push({ name: 'workstreams' })
      }
    }

    onMounted(async () => {
      await loadTask()
      await loadModels()
      await refreshData()
    })

    return {
      taskId,
      task,
      loading,
      assignedAgents,
      showAssignAgent,
      showEditTask,
      newAgentId,
      selectedAgentId,
      selectedModel,
      models,
      taskChatHistory,
      taskChatLoading,
      currentUserId,
      editedTask,
      goals,
      addAgent,
      removeAgent,
      completeTask,
      assignToMe,
      updateTask,
      refreshData,
      goBack,
      sendTaskChat,
      handleTaskQuickPrompt,
      handleTaskSettings,
      handleModelChange,
      getModelName
    }
  }
}
</script>

<style scoped>
.chat-view-container {
  padding: 2rem;
  max-width: 1600px;
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  background: white;
}

.btn-back {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
}

.btn-back:hover {
  background: #f8f9fa;
}

.header-title h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.chat-meta {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.model-badge,
.agent-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.model-badge {
  background: #e3f2fd;
  color: #1976d2;
}

.agent-badge {
  background: #e8f5e8;
  color: #2e7d32;
}

.header-center {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #37352f;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-subtitle {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.header-right {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.pill {
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 12px;
  color: #374151;
}

.muted {
  color: #9ca3af;
  font-size: 12px;
}

.loading-container {
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #6c757d;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  0% { transform: rotate(0); }
  100% { transform: rotate(360deg); }
}

.chat-content {
  flex: 1;
  height: calc(100vh - 200px);
  min-height: 500px;
  padding: 0 1rem 1rem 1rem;
}

.main-layout {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;
  height: 100%;
}

.context-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-y: auto;
}

.section-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
}

.section-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-row {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.info-label {
  font-weight: 500;
  color: #6c757d;
  min-width: 100px;
}

.info-value {
  margin: 0;
  color: #2c3e50;
  flex: 1;
}

.link {
  color: #007bff;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-badge.pending {
  background: #fff3cd;
  color: #856404;
}

.status-badge.todo {
  background: #d1ecf1;
  color: #0c5460;
}

.status-badge.in_progress {
  background: #d4edda;
  color: #155724;
}

.status-badge.done,
.status-badge.completed {
  background: #28a745;
  color: white;
}

.empty-state-small {
  text-align: center;
  padding: 1rem;
  color: #6c757d;
  font-size: 0.9rem;
}

.agents-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.agent-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.agent-icon {
  font-size: 1.5rem;
}

.agent-role {
  font-size: 12px;
  color: #6b7280;
}

.agent-info h4 {
  margin: 0 0 0.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
}

.agent-info p {
  margin: 0;
  font-size: 0.85rem;
  color: #6c757d;
}

.btn-remove {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.btn-remove:hover {
  background: #f8d7da;
}

@media (max-width: 1100px) {
  .main-grid {
    grid-template-columns: 1fr;
  }
}

.main-left,
.main-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  text-align: left;
}

.action-btn.primary {
  background: #007bff;
  color: white;
}

.action-btn.primary:hover {
  background: #0056b3;
}

.btn-chat-toggle {
  display: none;
}

.btn-close-drawer {
  display: none;
}

.action-btn.secondary {
  background: #6c757d;
  color: white;
}

.action-btn.secondary:hover {
  background: #545b62;
}

.chat-section {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 15, 15, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 10px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 12px 12px 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background: rgba(0, 0, 0, 0.1);
}

.modal-body {
  padding: 1.25rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #2c3e50;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 1rem;
  background: white;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

.btn-outline {
  background: white;
  border: 1px solid #e9ecef;
  color: #6c757d;
}

.btn-outline:hover {
  background: #f8f9fa;
}

.mt-2 {
  margin-top: 1rem;
}

/* Responsive design */
@media (max-width: 1024px) {
  .chat-view-container {
    padding: 1rem;
  }

  .main-layout {
    grid-template-columns: 1fr;
  }

  .chat-content {
    padding: 0;
  }
}

@media (max-width: 768px) {
  .chat-view-container {
    padding: 0.5rem;
  }

  .page-header {
    padding-bottom: 1rem;
  }

  .header-title h1 {
    font-size: 1.5rem;
  }

  .header-actions {
    flex-direction: column;
  }

  .chat-content {
    height: calc(100vh - 140px);
    padding: 0;
  }
}
</style>
